import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";
import { FC, memo, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { runPrefixMethod } from "@/utils/full-screen";

import { Icon } from "../icon";
import styles from "./index.module.less";
interface IPDFPreviewProps {
  pdfUrl: string;
  minScale?: number;
  maxScale?: number;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PDFPreview: FC<IPDFPreviewProps> = ({
  pdfUrl,
  minScale = 0.5,
  maxScale = 5,
}) => {
  const [numPages, setNumPages] = useState<number>();

  const [scale, setScale] = useState<number>(1);

  const [fullscreen, setFullscreen] = useState(false);

  const pdfScreenRef = useRef<HTMLDivElement>(null);

  let downScrollLeft = 0;

  let downClientX = 0;

  let downScrollTop = 0;

  let downClientY = 0;

  let isMoving = false;

  const handleZoomIn = () => {
    if (scale < maxScale) setScale((prevScale) => prevScale + 0.1);
  };

  const handleZoomOut = () => {
    if (scale > minScale) setScale((prevScale) => prevScale - 0.1);
  };

  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
    if (!fullscreen) {
      runPrefixMethod(
        document.querySelector("#pdf-review-screen"),
        "RequestFullScreen"
      );
    } else {
      setScale(1);
      runPrefixMethod(document, "CancelFullScreen");
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: PDFDocumentProxy) => {
    setNumPages(numPages);
  };

  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    isMoving = true;

    if (pdfScreenRef.current) {
      downClientX = event.clientX;
      downScrollLeft = pdfScreenRef.current.scrollLeft;
      downClientY = event.clientY;
      downScrollTop = pdfScreenRef.current.scrollTop;
      pdfScreenRef.current.style.cursor = "move";
    }
  };

  const throttle = <T extends (...args: any[]) => void>(
    callback: T,
    delay: number
  ) => {
    let timeoutId: NodeJS.Timeout | null = null;

    let requestId: number | null = null;

    const throttled = (...args: Parameters<T>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      requestId = requestAnimationFrame(() => {
        callback(...args);
        requestId = null;
      });
    };

    return (...args: Parameters<T>) => {
      if (!timeoutId && !requestId) {
        timeoutId = setTimeout(() => throttled(...args), delay);
      }
    };
  };

  const scrollCallback = throttle((clientX: number, clientY: number) => {
    if (isMoving && pdfScreenRef.current) {
      pdfScreenRef.current.scrollLeft =
        downScrollLeft + (downClientX - clientX);
      pdfScreenRef.current.scrollTop = downScrollTop + (downClientY - clientY);
    }
  }, 16);

  const handleMouseMove = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => scrollCallback(event.clientX, event.clientY);

  const handleMouseUp = () => {
    isMoving = false;
    if (pdfScreenRef.current) {
      pdfScreenRef.current.style.cursor = "default";
    }
  };

  // 添加监听媒体区域鼠标移动, 控制controls显隐
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const screenElemnt = document.querySelector("#pdf-review-screen");

    const controls: HTMLDivElement | null =
      document.querySelector("#pdf-controls");

    const onDisplayControls = () => {
      clearTimeout(timer);
      if (controls) {
        controls.style.display = "flex";
        timer = setTimeout(() => {
          controls.style.display = "none";
        }, 3000);
      }
    };

    if (screenElemnt) {
      screenElemnt.addEventListener("mousemove", onDisplayControls);
    }

    onDisplayControls();

    return () => {
      clearTimeout(timer);
      screenElemnt?.removeEventListener("mousemove", onDisplayControls);
    };
  }, []);

  return (
    <div className={styles["pdf-review-screen"]} id="pdf-review-screen">
      <div
        className={styles["pdf"]}
        ref={pdfScreenRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <div className={styles["content"]}>
          <Document
            file={pdfUrl}
            onLoadSuccess={(res) => onDocumentLoadSuccess(res)}
            renderMode="canvas"
            className={styles["canvas"]}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
              />
            ))}
          </Document>
        </div>
      </div>
      <div className={styles["controls"]} id="pdf-controls">
        <Icon icon="addCircle" width={24} height={24} onClick={handleZoomIn} />
        <Icon
          icon="minusCircle"
          width={24}
          height={24}
          onClick={handleZoomOut}
        />
        <Icon
          icon="screamFull"
          width={24}
          height={24}
          onClick={toggleFullscreen}
        />
      </div>
    </div>
  );
};

export default memo(PDFPreview);
